import {
  useGuardedEffect,
  useNavigateWithSearchParams,
} from "@p0security/frontend-core";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type {
  ConfigOf,
  InstallSpec,
  Instructions as InstructionsType,
  ItemComponent,
  ItemConfigOf,
} from "@p0security/shared/install/types";
import type { IntegrationStatus } from "@p0security/shared/integrations/shared";
import { Flex, Spin, Typography } from "@p0security/ui";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { FrontendInstallContext } from "../types";
import type { ComponentProps } from "./Component";
import { NEW_SENTINEL } from "./Component";
import { ConfigForm } from "./ConfigForm";
import { DiffInstructions } from "./DiffInstructions";
import { Instructions } from "./Instructions";
import type { NewLocationState } from "./New";
import { evaluate } from "./util.ts";

export type ConfigProps<T extends InstallSpec> = ComponentProps<T> & {
  component: ItemComponent<any>;
  context: FrontendInstallContext<ConfigOf<T>>;
  id: string;
  item: ItemConfigOf<T[string]>;
  onInstall?: (config: any) => void;
  onItemStateChange?: (state: IntegrationStatus) => void;
};

/**
 * The install API path of an integration's config, or of one of its items.
 *
 * `componentKey` and `id` are URL-encoded, so an item key a customer typed
 * stays one path segment even when it holds `?`, `#`, `%` or `/`.
 */
export const configPath = (props: {
  integration: string;
  componentKey?: string;
  id?: string;
}) => {
  const { id, integration, componentKey } = props;
  return `integrations/${integration}/config${
    componentKey ? `/${encodeURIComponent(componentKey)}` : ""
  }${id ? `/${encodeURIComponent(id)}` : ""}`;
};

export const Config = <T extends InstallSpec>(props: ConfigProps<T>) => {
  const {
    authFetch,
    canEdit,
    component,
    componentKey,
    context,
    id,
    installer,
    integration,
    registration,
    item,
    onInstall,
    onWarning,
  } = props;

  const navigate = useNavigateWithSearchParams();
  const [last, setLast] = useState<any>();
  const [staged, setStaged] = useState<any>();
  const [instructionState, setInstructionState] = useState<Record<string, any>>(
    {}
  );

  const title = useMemo(
    () => (item.label ? `${item.label} (${id})` : id),
    [id, item]
  );

  const instructions = useMemo(
    () =>
      installer[componentKey]?.instructions?.(
        context,
        id,
        item as any,
        item,
        instructionState,
        setInstructionState
      ),
    [componentKey, context, id, installer, instructionState, item]
  );

  const stageConfig = useCallback(
    async (config: any) => {
      setLast(config);
      setStaged(config);
    },
    [setStaged]
  );

  const submit = useCallback(
    async (config: any, step: "configure" | "verify", stayOnItem?: boolean) => {
      // Clear any warning left over from a previous submit before this one
      // resolves, so a stale warning never sits beside a fresh error.
      onWarning(undefined);
      const response = await authFetch(
        `${configPath({ integration, componentKey, id })}/${step}`,
        {
          method: "POST",
          json: config,
        }
      );

      if (response?.ok) {
        const body = await response.json();
        const warning = body?.metadata?.warning;
        onWarning(typeof warning === "string" ? warning : undefined);
      }

      if (!stayOnItem && response?.ok) {
        // If the last step is configure and we have a 200,
        // then the item was installed
        // Return early so the onInstall callback can navigate
        // without being overridden
        if (onInstall && step === "configure") {
          onInstall(config);
          return;
        }
        navigate("..", { relative: "path" });
      }
    },
    [authFetch, integration, componentKey, id, onInstall, onWarning, navigate]
  );

  const verify = useCallback(
    async () => await submit(item, "verify", true),
    [item, submit]
  );
  // Returns to the identifier form with this item's values filled in. The
  // staged item stays until the form is submitted again: the same identifier
  // re-assembles it, and a different one replaces it.
  const back = useCallback(() => {
    const state: NewLocationState = { stagedId: id };
    navigate(`../${NEW_SENTINEL}`, { relative: "path", state });
  }, [id, navigate]);
  // Back only appears where "Add" would: a new identifier creates a new item
  const canGoBack = useMemo(
    () =>
      canEdit &&
      id !== SINGLETON_ITEM &&
      (!component.maxItems ||
        component.maxItems >
          Object.keys(props.config?.[componentKey] ?? {}).length),
    [canEdit, component.maxItems, componentKey, id, props.config]
  );
  const logError = useCallback((error: any) => console.error(error), []);

  // If in stage and there are no set-up instructions, automatically advance to configure step
  useGuardedEffect(
    () => async () => {
      if (item.state === "stage" && !instructions) {
        await verify();
      }
    },
    [item.state, instructions, verify],
    logError
  );

  const finish = useCallback(async () => {
    await submit(staged, "configure", false);
    setStaged(null);
  }, [staged, submit]);

  // Wrapped, void-returning versions for JSX event-handler-shaped props
  // (`onNext`, `onFinish`, `setConfig`), which are typed to return void.
  const onVerify = useCallback(() => void verify(), [verify]);
  const onFinish = useCallback(() => void finish(), [finish]);
  const onStageConfig = useCallback(
    (value: any) => void stageConfig(value),
    [stageConfig]
  );

  const config = useMemo(() => last ?? (item as any), [item, last]);

  const itemKind = useMemo(
    () => evaluate(registration.itemKind, componentKey),
    [componentKey, registration]
  );

  return (
    <Flex vertical gap={8}>
      {
        // TODO: Fix typing
        installer[componentKey]?.preamble?.(context, id, config) as ReactNode
      }
      <Typography.Title level={5}>
        Configuring
        {id === SINGLETON_ITEM ? "" : ` ${itemKind} ${title}`}
      </Typography.Title>
      {item.state === "stage" ? (
        instructions ? (
          <Instructions
            commandDisplay={props.siblings.CommandDisplay}
            instructions={instructions as InstructionsType<ReactNode>}
            isFetching={props.isFetching}
            onBack={canGoBack ? back : undefined}
            onNext={onVerify}
          />
        ) : (
          <Spin />
        )
      ) : staged ? (
        <DiffInstructions
          {...props}
          before={item}
          after={staged}
          installer={installer[componentKey] as any}
          onFinish={onFinish}
        />
      ) : (
        <ConfigForm
          config={config}
          context={context}
          allDisabled={!canEdit}
          // TODO: Fix typing
          installer={installer[componentKey] as any}
          isFetching={props.isFetching}
          id={id}
          item={config}
          schema={component.schema}
          setConfig={onStageConfig}
          step="configure"
        />
      )}
    </Flex>
  );
};
